// ============================================================================
// src/app/bootstrap/runtimeInit.ts — the pre-render and post-render boot steps.
// ============================================================================
// WHY THIS EXISTS
// src/main.jsx performed five side-effecting boot steps at module scope, above
// 4,000 lines of application code, and one more after root.render(). They are
// not application logic: they touch localStorage, history, the manifest link,
// the service worker, a window global, and the static splash element. Pulling
// them here makes them nameable, individually testable, and — for the first
// time — orderable by something other than physical position in a 4,458-line
// file.
//
// CALL ORDER IS A CONTRACT, NOT A CONVENIENCE (from JOIN-MANIFEST-B1.md §2):
//
//   1. initRuntime()        MUST run BEFORE the first render. The hash-compat
//                           rewrite has to land before BrowserRouter reads
//                           pathname, or a legacy `#webforms` bookmark boots to
//                           home. Nothing here is awaited: the function is
//                           synchronous by construction, service-worker
//                           registration defers itself to `window` load, and
//                           the XLSX import is lazy. There is no promise to
//                           await and none should be introduced.
//   2. root.render(...)
//   3. retireBootLoader()   MUST run STRICTLY AFTER root.render(). It books two
//                           animation frames on purpose so the first frame of
//                           real React content is on screen before the splash
//                           fades.
//
// Every step is independently fail-soft: a locked-down browser with no
// localStorage, no History API, or no DOM must still boot. A step that cannot
// run is skipped, never fatal — but each is scoped so a skip cannot be mistaken
// for success by a caller that cares (initRuntime returns a step report).
//
// CAPABILITIES ARE INJECTED with real-global defaults. Production calls
// initRuntime() with no arguments and gets exactly the previous behaviour; the
// unit tests pass fakes and assert each step in isolation without a DOM.
// ============================================================================

import {HASH_COMPAT} from '../../lib/routes.js';
import {registerAppServiceWorker} from '../../lib/serviceWorkerRegistration.js';
import {replaceBrowserHistoryState} from '../navigation/browserHistorySeam.js';
import type {BrowserHistoryWriter} from '../navigation/browserHistorySeam.js';

// A non-null, non-array object. Used where a value's shape is genuinely open
// (a dynamically imported module namespace).
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const BOOT_LOADER_ELEMENT_ID = 'wcf-boot-loader';
export const BOOT_LOADER_FADE_MS = 350;

const STEP = Object.freeze({DONE: 'done', SKIPPED: 'skipped'});
export const BOOT_STATUS = Object.freeze({IDLE: 'idle', READY: 'ready', ABORTED: 'aborted'});

/** The per-step outcome. A skip is reported, never silently folded into success. */
export type StepResult = 'done' | 'skipped';

/** The three boot states. retireBootLoader acts on READY and nothing else. */
export type BootStatus = 'idle' | 'ready' | 'aborted';

// Module-scoped boot state, mirroring the coordinator model in
// JOIN-MANIFEST-B1.md §2: start() runs, then retireBootLoader() consults what
// start() concluded. It is deliberately NOT a parameter — main.tsx must not be
// able to retire the splash by passing the wrong argument.
//
// Annotated with the full union rather than inferred from the initial value:
// inference would fix the field at the literal 'idle' and make every later
// transition an error instead of a state machine.
const boot: {status: BootStatus} = {status: BOOT_STATUS.IDLE};

export function getBootStatus(): BootStatus {
  return boot.status;
}

// Test-only. Production never resets boot state; the guard asserts this is
// referenced nowhere outside this module's own tests.
export function resetBootStatusForTests(): void {
  boot.status = BOOT_STATUS.IDLE;
}

// ── Injected capability shapes ──
//
// Each is the NARROWEST subset of the real global this module actually calls,
// not a mirror of the DOM lib type. That is what lets the unit tests pass plain
// fakes without a DOM, which is the whole point of the injection.

/** localStorage, as far as the legacy-cache purge uses it. */
export type StorageLike = {
  readonly length: number;
  key: (index: number) => string | null;
  removeItem: (key: string) => void;
};

/** window.location, for the hash shim and the manifest sync. */
export type LocationLike = {
  hash: string;
  pathname: string;
};

/** window.history, for the one-time hash rewrite. */
export type HistoryLike = BrowserHistoryWriter;

/** The window-ish object the XLSX bridge hangs itself off. */
export type XlsxGlobal = {
  XLSX?: unknown;
  _wcfXLSXPromise?: Promise<unknown>;
  _wcfLoadXLSX?: () => Promise<unknown>;
};

// The three globals THIS module installs, declared where they are assigned —
// the same move src/lib/supabase.ts makes for `window.sb`. All optional,
// because they genuinely do not exist until installXlsxBridge has run: the
// existing call sites are written as `await window._wcfLoadXLSX()` behind the
// boot, and typing them as always-present would erase the difference between
// "the bridge is installed" and "it is not".
declare global {
  interface Window {
    XLSX?: unknown;
    _wcfXLSXPromise?: Promise<unknown>;
    _wcfLoadXLSX?: () => Promise<unknown>;
  }
}

/** The legacy `#hash` -> path map applyHashCompat consults. */
export type HashCompatMap = Readonly<Record<string, string>>;

/** Everything initRuntime may be handed instead of reaching for a real global. */
export type RuntimeCapabilities = {
  hashCompatMap?: HashCompatMap;
  registerServiceWorker?: () => void;
  syncManifest?: (pathname: string) => void;
  storage?: StorageLike | null;
  location?: LocationLike | null;
  history?: HistoryLike | null;
  globalTarget?: XlsxGlobal | null;
};

// `doc` is typed as the real Document rather than a hand-written subset,
// because the DOM's own removeChild/parentNode relationship is what this code
// relies on and a narrowed stand-in cannot reproduce it (removeChild is
// generic over Node). The unit tests inject plain fakes; they are JavaScript,
// so the runtime remains injectable exactly as before.

/** requestAnimationFrame, as far as the two-frame wait uses it. */
export type FrameFn = (callback: () => void) => unknown;

/** setTimeout, as far as the fade delay uses it. */
export type TimerFn = (callback: () => void, ms: number) => unknown;

/** The per-step report initRuntime returns. A caller can tell a skip from a success. */
export type BootStepReport = {
  legacyStorageCleanup?: StepResult;
  hashCompat?: StepResult;
  manifestSync?: StepResult;
  serviceWorker?: StepResult;
  xlsxBridge?: StepResult;
};

// ── ONE-TIME LEGACY BABEL-CACHE CLEANUP ──
// Pre-Vite versions cached compiled JSX in localStorage under wcf-babel-*
// keys (~600 KB per user). With Vite there's no in-browser transpile, so
// these are dead weight. Idempotent purge on every mount; safe to leave
// in forever even after every existing user has reloaded once.
export function purgeLegacyBabelCache(storage: StorageLike): StepResult {
  try {
    for (let i = storage.length - 1; i >= 0; i--) {
      const k = storage.key(i);
      if (k && k.startsWith('wcf-babel-')) storage.removeItem(k);
    }
    return STEP.DONE;
  } catch (e) {
    /* localStorage disabled — fine */
    return STEP.SKIPPED;
  }
}

// ── PHASE 3.3 — LEGACY HASH-BOOKMARK COMPAT SHIM ──
// Pre-Phase-3 users may have saved /#weighins, /#addfeed, /#webforms
// URLs in browser bookmarks, Slack links, or printed materials. The
// router only reads pathname, not hash, so we rewrite the URL here
// synchronously BEFORE root.render() runs — otherwise BrowserRouter
// would read pathname='/' and the adapter would snap to home.
//
// The supabase password-recovery hash (/#access_token=...&type=recovery)
// is deliberately NOT rewritten — SetPasswordScreen still parses it
// from window.location.hash for backward compat with the email template.
export function applyHashCompat(
  location: LocationLike,
  history: HistoryLike,
  hashCompatMap: HashCompatMap = HASH_COMPAT,
): StepResult {
  try {
    const h = location.hash;
    if (h && hashCompatMap[h]) {
      replaceBrowserHistoryState(history, null, hashCompatMap[h]);
      return STEP.DONE;
    }
    return STEP.SKIPPED;
  } catch (e) {
    /* no history API / locked-down browser — skip */
    return STEP.SKIPPED;
  }
}

// ── MANIFEST HREF SWAP ──
// Three static manifests live in public/:
//   - manifest.webmanifest          (start_url /)         — authenticated app root
//   - manifest-dailys.webmanifest   (start_url /dailys)   — operator daily-reports hub
//   - manifest-equipment.webmanifest (start_url /equipment) — equipment/fueling hub
//   - manifest-pasture.webmanifest  (start_url /pasture-map) — pasture map field hub
// The link[rel="manifest"] href tracks the active hub:
//   - /equipment* or /fueling*    → equipment manifest
//   - /dailys* or /webforms*      → dailys manifest
//   - /pasture-map*               → pasture manifest
//   - everything else             → root manifest
// The HTML <link> tag is the install source-of-truth (Add to Home Screen
// snapshots it at HTML parse time), so Netlify _redirects routes hub URLs
// to dedicated HTMLs that bake in the right manifest link before any JS
// runs. This helper is defensive runtime sync: it also runs at module
// scope (correct href before first paint when arriving via the SPA
// fallback) AND inside an App useEffect keyed on location.pathname (so
// SPA navigation between hubs keeps the link in sync without a reload).
export function applyManifestHref(pathname: string): void {
  try {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    let next = '/manifest.webmanifest';
    if (pathname.startsWith('/equipment') || pathname.startsWith('/fueling')) {
      next = '/manifest-equipment.webmanifest';
    } else if (pathname.startsWith('/dailys') || pathname.startsWith('/webforms')) {
      next = '/manifest-dailys.webmanifest';
    } else if (pathname.startsWith('/pasture-map')) {
      next = '/manifest-pasture.webmanifest';
    }
    if (link.getAttribute('href') !== next) link.setAttribute('href', next);
  } catch (e) {
    /* no DOM / locked-down browser — skip */
  }
}

// ── LAZY LOAD SHEETJS ──
// SheetJS (xlsx) is ~600KB minified and only used when opening processor
// reports / running bulk imports. Defer loading until first use via dynamic
// ESM import. Same window._wcfLoadXLSX API as the pre-Vite CDN-script-tag
// loader so the existing call sites (`await window._wcfLoadXLSX()` followed
// by `XLSX.X`) don't need to change.
export function installXlsxBridge(
  globalTarget: XlsxGlobal,
  importXlsx: () => Promise<unknown> = () => import('xlsx'),
): StepResult {
  globalTarget._wcfLoadXLSX = function () {
    if (globalTarget.XLSX) return Promise.resolve(globalTarget.XLSX);
    if (globalTarget._wcfXLSXPromise) return globalTarget._wcfXLSXPromise;
    globalTarget._wcfXLSXPromise = importXlsx().then((m) => {
      // The namespace object is the module itself unless a bundler wrapped it
      // in a default export. Both shapes are stored verbatim, exactly as the
      // pre-Vite CDN loader did — call sites read `XLSX.<member>` off whichever
      // arrives, so nothing here may narrow it.
      const loaded: unknown = m;
      globalTarget.XLSX = (isPlainRecord(loaded) && loaded.default) || loaded;
      return globalTarget.XLSX;
    });
    return globalTarget._wcfXLSXPromise;
  };
  return STEP.DONE;
}

// Fade out the static boot loader after React's first paint. Two RAFs to
// ensure the first frame containing real React content is on screen before
// the spinner disappears (matches the original bootstrap's fadeOutLoader).
//
// MUST be called AFTER root.render(). Calling it before would fade the splash
// over an empty document.
export function retireBootLoader(
  doc: Document | null = typeof document === 'undefined' ? null : document,
  frame?: FrameFn | null,
  timer?: TimerFn | null,
): StepResult {
  // REFUSES unless boot actually reached READY (JOIN-MANIFEST-B1.md §2.4). An
  // aborted or never-started boot deliberately leaves the splash on screen — it
  // is the only user-visible signal the app did not come up, and fading it would
  // present a blank, apparently-working page instead. Calling this
  // unconditionally from the root is therefore correct.
  if (boot.status !== BOOT_STATUS.READY) return STEP.SKIPPED;
  const raf = frame || (typeof requestAnimationFrame === 'function' ? requestAnimationFrame : null);
  const wait = timer || (typeof setTimeout === 'function' ? setTimeout : null);
  if (!doc || !raf || !wait) return STEP.SKIPPED;
  raf(() => {
    raf(() => {
      const loader = doc.getElementById(BOOT_LOADER_ELEMENT_ID);
      if (loader) {
        loader.classList.add('fade-out');
        wait(() => {
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        }, BOOT_LOADER_FADE_MS);
      }
    });
  });
  return STEP.DONE;
}

// The single pre-render entry point. Synchronous, never throws, never awaited.
// Returns a per-step report so a caller (or a test) can tell a skip from a
// success — a half-wired boot must not look identical to a healthy one.
export function initRuntime(capabilities: RuntimeCapabilities = {}): {steps: BootStepReport; status: BootStatus} {
  const {
    hashCompatMap = HASH_COMPAT,
    registerServiceWorker = registerAppServiceWorker,
    syncManifest = applyManifestHref,
  } = capabilities;

  // ACQUIRING a global can itself throw — this is not the same risk as USING
  // one. In a sandboxed iframe or a browser with site data blocked, reading
  // `localStorage` raises SecurityError at the property access. Resolving that
  // as a destructuring default put the read outside every try-block in this
  // module, so a locked-down browser aborted the whole boot before the first
  // step could catch anything. Each acquisition is guarded on its own, and a
  // capability that cannot be obtained is simply absent — the same as one that
  // was never injected.
  const acquire = <T>(get: () => T | null | undefined): T | null => {
    try {
      return get() ?? null;
    } catch {
      return null;
    }
  };
  const has = (key: keyof RuntimeCapabilities): boolean => Object.prototype.hasOwnProperty.call(capabilities, key);
  const storage = has('storage') ? (capabilities.storage ?? null) : acquire(() => globalThis.localStorage);
  const location = has('location') ? (capabilities.location ?? null) : acquire(() => globalThis.window?.location);
  const history = has('history') ? (capabilities.history ?? null) : acquire(() => globalThis.window?.history);
  const globalTarget = has('globalTarget') ? (capabilities.globalTarget ?? null) : acquire(() => globalThis.window);

  const steps: BootStepReport = {};
  try {
    steps.legacyStorageCleanup = storage ? purgeLegacyBabelCache(storage) : STEP.SKIPPED;
    // ORDER-CRITICAL: the rewrite must precede any router read of pathname.
    steps.hashCompat = location && history ? applyHashCompat(location, history, hashCompatMap) : STEP.SKIPPED;
    if (location) {
      syncManifest(location.pathname);
      steps.manifestSync = STEP.DONE;
    } else {
      steps.manifestSync = STEP.SKIPPED;
    }
    registerServiceWorker();
    steps.serviceWorker = STEP.DONE;
    steps.xlsxBridge = globalTarget ? installXlsxBridge(globalTarget) : STEP.SKIPPED;
  } catch {
    // A step that throws ABORTS the boot. It does not rethrow — main.tsx has no
    // useful recovery — but it must not be mistaken for a healthy boot, so the
    // splash is never retired and the failure stays on screen.
    boot.status = BOOT_STATUS.ABORTED;
    return {steps, status: boot.status};
  }
  boot.status = BOOT_STATUS.READY;
  return {steps, status: boot.status};
}
